import React from "react";
import styled, { css } from "styled-components";
import { Table } from "@cinesys/ui/components";
import { GamebookPlayLite } from "./service";
import { formatYardage, quarterLabel } from "./gamebookRowFormat";

/**
 * Thin `Table` wrapper for a small windowed gamebook picker (Phase A's P001-P010 start picker,
 * Phase B's per-anchor ±4 `gamebookWindow`) — columns `UID · Qtr · Summary(+yardage) · pick tag`,
 * rows keyed by uid, Spotter's suggested pick highlighted via inset box-shadow (row selection
 * convention, never a border — borders shift the grid) plus an amber "Spotter's pick" badge.
 *
 * Point 7 (mini Gamebook rows): UID/quarter-chip/yardage formatting mirror `GamebookScreen.tsx`'s
 * own `UidCell`/`SitCell .q`/`PlayCell .yd` recipes exactly (smaller font throughout — this reads
 * as a step down even from Gamebook's own already-compact row) rather than inventing a new look.
 * Not expandable, no flag bar — already true, nothing to add.
 *
 * KEYBOARD ACCESS: `@cinesys/ui`'s `Table` wires row selection via `onClick` only — confirmed by
 * reading its source (`cinesys-ui/packages/ui/src/components/Table.tsx`), no `tabIndex`/`role`/
 * `onKeyDown` anywhere near row rendering. This wrapper adds its own via `Row.domRef` (the one
 * per-row DOM escape hatch `Table` exposes) rather than forking the component.
 */

const Wrap = styled.div`
  border: 1px solid var(--spotterLine);
  border-radius: 9px;
  overflow: hidden;
  font-size: 11.5px;
`;

const Cell = styled.span<{ $wrap?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--spotterInk);

  /* The play summary can run long — truncating it with an ellipsis was the reported bug
     ("can't see the entire write out of the play"); wrap instead so the row just grows
     (Table's own row uses min-height, not a fixed height — see GamebookPickerTable's file doc
     comment for why that's safe here). */
  ${({ $wrap }) =>
    $wrap &&
    css`
      white-space: normal;
      text-overflow: clip;
      overflow: visible;
      word-break: break-word;
      line-height: 1.35;
      padding: 4px 0;
    `}
`;

/** `GamebookScreen.tsx`'s `UidCell b { color: var(--spotterAccent) }` — "UID in its distinct
 *  colour" (Point 7). */
const UidCell = styled(Cell)`
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;
  color: var(--spotterAccent);
`;

/** `GamebookScreen.tsx`'s `SitCell .q` chip, verbatim. */
const QtrChip = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;
  font-size: 11px;
  color: var(--spotterInk);
  background: var(--spotterCodeBg);
  padding: 2px 6px;
  border-radius: 5px;
`;

/** `GamebookScreen.tsx`'s `PlayCell .yd`/`.pos`/`.neg`/`.zero`, verbatim — an inline prefix on the
 *  summary cell rather than its own column, same as the full Gamebook row. Omitted entirely (not
 *  a placeholder dash) when a play has no yardage on record — see `gamebookRowFormat.ts`. */
const YardageTag = styled.span<{ $tone: "pos" | "neg" | "zero" }>`
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;
  font-size: 11.5px;
  margin-right: 7px;
  color: ${({ $tone }) =>
    $tone === "pos"
      ? "var(--spotterOk)"
      : $tone === "neg"
      ? "var(--spotterDanger)"
      : "var(--spotterInkFaint)"};
`;

const PickTag = styled.span`
  font-size: 9.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--spotterAccent);
  white-space: nowrap;
`;

const COLUMN_WIDTHS = ["64px", "48px", "1fr", "110px"];

export interface GamebookPickerTableProps {
  plays: GamebookPlayLite[];
  /** Gets both the "Spotter's pick" badge and the row-selected visual treatment (inset
   *  box-shadow) — Spotter's own suggestion. Neighbor-nav (`AnchorStage.tsx`) swaps which video
   *  plays instead of moving this highlight, so this table only ever highlights the suggestion. */
  suggestedUid?: string;
  onSelect: (play: GamebookPlayLite) => void;
  rowHeight?: number;
}

const GamebookPickerTable = ({
  plays,
  suggestedUid,
  onSelect,
  rowHeight = 40,
}: GamebookPickerTableProps) => {
  return (
    <Wrap>
      <Table
        columns={COLUMN_WIDTHS.map((width) => ({ width }))}
        columnGrid
        rowHeight={rowHeight}
        rowPadding="6px 11px"
        rows={plays.map((play) => {
          const isSuggested = play.uid === suggestedUid;
          const handleSelect = () => onSelect(play);
          return {
            id: play.uid,
            onClick: handleSelect,
            style: isSuggested
              ? {
                  boxShadow: "inset 0 0 0 1px var(--spotterAccentLine)",
                  background: "var(--spotterAccentSoft)",
                }
              : undefined,
            // Table's own row click has no keyboard equivalent (see file doc comment) — attach
            // tabIndex/role/onKeyDown directly to the row's DOM node via this escape hatch.
            domRef: (el: HTMLElement | null) => {
              if (!el) {
                return;
              }
              el.tabIndex = 0;
              el.setAttribute("role", "button");
              el.setAttribute(
                "aria-label",
                `Pick play ${play.uid}: ${play.summary}`
              );
              el.onkeydown = (e: KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelect();
                }
              };
            },
            element: () => {
              const yardage = formatYardage(play.yardsGainedOrLost);
              return (
                <>
                  <UidCell>{play.uid}</UidCell>
                  <Cell>
                    <QtrChip>{quarterLabel(play.quarter)}</QtrChip>
                  </Cell>
                  <Cell $wrap>
                    {yardage && (
                      <YardageTag $tone={yardage.tone}>
                        {yardage.label}
                      </YardageTag>
                    )}
                    {play.summary}
                  </Cell>
                  <span>
                    {isSuggested && <PickTag>Spotter's pick</PickTag>}
                  </span>
                </>
              );
            },
          };
        })}
      />
    </Wrap>
  );
};

export default GamebookPickerTable;
